import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../../utils/axiosInstance';
import './Login.css';
import { runSecurityTests } from '../../utils/securityTest';

function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleSubmit = async event => {
    event.preventDefault();
    
    // Trim and validate email
    const trimmedEmail = email.trim();
    
    // Run security tests
    const securityResults = runSecurityTests(trimmedEmail);
    if (Object.values(securityResults).some(result => result)) {
      setMessage("Invalid input detected");
      return;
    }
    
    if (!trimmedEmail) {
      setMessage('Please enter your email address');
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setMessage('Please enter a valid email address');
      return;
    }

    try {
      const response = await axiosInstance.post('/users/sendPasswordResetEmail', { email: trimmedEmail });
      setMessage("If an account with that email exists, we have sent a password reset email.");
    } catch (error) {
      setMessage("Failed to send password reset email. Please try again.");
    }
  };

  // Render modal into document.body so overlay is full viewport and centered
  // (avoids parent layout/transform affecting position)
  const modalContent = (
    <div className="modal-overlay">
      <div ref={overlayRef} className="login-box">
        <h1 className="login-title">Reset Password</h1>
        <form onSubmit={handleSubmit} className="login-form">
          <input
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            placeholder="Email"
            className="login-input"
          />
          <div className="modal-buttons">
            <button className="new-account-button" type="submit">Send Reset Link</button>
            <button className="forgotPassword-button" type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>
        {message && (
          <div className={`forgot-password-modal-message ${message.startsWith('If an account') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ForgotPasswordModal;
